<template>
  <v-app-bar id="default-app-bar" fixed app color="drawer" :clipped-left="$vuetify.rtl" :clipped-right="!$vuetify.rtl">
    <v-app-bar-nav-icon class="hidden-md-and-up" @click="drawer = !drawer" />

    <default-drawer-toggle class="hidden-sm-and-down" />

    <v-spacer />

    <!-- Central section with patrols and events -->
    <!-- My Events button -->
    <v-btn icon @click="navigateToEvents('dashboard_my')" title="Moje zdarzenia">
      <v-badge :content="myEventsCount" :value="myEventsCount > 0" color="info" overlap bordered
        transition="slide-x-transition" offset-x="8" offset-y="8">
        <v-icon>mdi-clipboard-account</v-icon>
      </v-badge>
    </v-btn>

    <!-- Unassigned Events button -->
    <v-btn icon @click="navigateToEvents('dashboard_unassigned')" title="Nieprzypisane zdarzenia" class="ml-2"
      :class="{ 'pulse-active': unassignedEventsCount > 0 }">
      <v-badge :content="unassignedEventsCount" :value="unassignedEventsCount > 0" color="warning" overlap bordered
        transition="slide-x-transition" offset-x="8" offset-y="8">
        <v-icon>mdi-clipboard-alert</v-icon>
      </v-badge>
    </v-btn>

    <!-- Other Events button -->
    <v-btn icon @click="navigateToEvents('dashboard_other')" title="Zdarzenia innych" class="ml-2">
      <v-badge :content="notMyEventsCount" :value="notMyEventsCount > 0" color="grey" overlap bordered
        transition="slide-x-transition" offset-x="8" offset-y="8">
        <v-icon>mdi-clipboard-multiple</v-icon>
      </v-badge>
    </v-btn>

    <!-- Event changes and closures requiring acknowledgement -->
    <v-btn icon @click="navigateToEvents('dashboard_my')" title="Moje nieprzeczytane zdarzenia"
      aria-label="Przejdz do moich nieprzeczytanych zdarzen" class="ml-2"
      :class="{ 'pulse-active': myClosedPendingAckCount > 0 }">
      <v-badge :content="myEventSignalCount" :value="myEventSignalCount > 0"
        :color="myClosedPendingAckCount > 0 ? 'error' : 'deep-orange'" overlap bordered transition="slide-x-transition"
        offset-x="8" offset-y="8">
        <v-icon>mdi-bell-badge</v-icon>
      </v-badge>
    </v-btn>

    <v-divider vertical class="mx-3"></v-divider>
    <!-- Patrols button with badge for active patrols -->
    <v-btn icon @click="togglePatrolDrawer" :color="patrolDrawerOpen ? 'primary' : ''" :title="getPatrolTooltip()"
      :class="{ 'pulse-active': hasPatrolsWithErrors }">
      <v-badge :content="activePatrolsCount" :value="activePatrolsCount > 0"
        :color="hasPatrolsWithErrors ? 'error' : 'success'" overlap bordered transition="slide-x-transition"
        offset-x="8" offset-y="8">
        <v-icon>{{ getPatrolIcon() }}</v-icon>
      </v-badge>
    </v-btn>
    <v-spacer />

    <v-btn icon @click="themeDialogOpen = true" class="ml-2" title="Wybór motywu">
      <v-icon>mdi-palette</v-icon>
    </v-btn>

    <v-btn icon @click="toggleDarkMode" class="ml-2">
      <v-icon>{{ dark ? 'mdi-white-balance-sunny' : 'mdi-moon-waning-crescent' }}</v-icon>
    </v-btn>
    <!-- <default-search class="hidden-sm-and-down" />

    <default-go-home />

    <default-notifications /> -->

    <default-account />

    <theme-selector v-model="themeDialogOpen" />
  </v-app-bar>
</template>

<script>
// Utilities
import { get, sync } from 'vuex-pathify'
import { isPatrolDelayed } from '@/store/modules/task'
import { playNotification } from '@/utils/notificationSound'

export default {
  name: 'DefaultBar',

  data: () => ({
    themeDialogOpen: false,
  }),

  components: {
    ThemeSelector: () =>
      import(
        /* webpackChunkName: "theme-selector" */
        '@/components/ThemeSelector'
      ),
    DefaultAccount: () =>
      import(
        /* webpackChunkName: "default-account" */
        './widgets/Account'
      ),
    DefaultDrawerToggle: () =>
      import(
        /* webpackChunkName: "default-drawer-toggle" */
        './widgets/DrawerToggle'
      ),
    // DefaultGoHome: () => import(
    //   /* webpackChunkName: "default-go-home" */
    //   './widgets/GoHome'
    // ),
    // DefaultNotifications: () => import(
    //   /* webpackChunkName: "default-notifications" */
    //   './widgets/Notifications'
    // ),
    // DefaultSearch: () => import(
    //   /* webpackChunkName: "default-search" */
    //   './widgets/Search'
    // ),
  },
  methods: {
    toggleDarkMode() {
      this.dark = !this.dark
    },
    togglePatrolDrawer() {
      this.patrolDrawer = !this.patrolDrawer
      this.$store.dispatch('user/update')
    },
    navigateToEvents(tabId) {
      this.$router.push({ path: '/events', hash: `#${tabId}` })
    },
    getTaskTypeName(taskType) {
      switch (taskType) {
        case 1:
          return 'Operator'
        case 2:
          return 'Patrol'
        case 3:
          return 'Serwis'
        default:
          return 'Nieznany'
      }
    },
    getPatrolIcon() {
      if (this.patrolDrawerOpen) {
        return 'mdi-shield-off'
      } else {
        if (this.activePatrolsCount > 0) {
          return 'mdi-shield-alert' // Shield with alert for active patrols
        } else if (this.patrolCount > 0) {
          return 'mdi-shield-account' // Regular shield with patrols
        } else {
          return 'mdi-shield-outline' // Empty shield when no patrols
        }
      }
    },
    getPatrolTooltip() {
      const action = this.patrolDrawerOpen ? 'Ukryj' : 'Pokaż'
      const count = this.patrolCount > 0 ? ` (${this.patrolCount})` : ''
      const activeInfo =
        this.activePatrolsCount > 0 ? ` - ${this.activePatrolsCount} aktywnych` : ''
      return `${action} panel patroli${count}${activeInfo}`
    },
  },

  computed: {
    ...sync('app', ['drawer']),
    ...sync('user', ['dark', 'patrolDrawer']),
    name: get('route/name'),
    patrolDrawerOpen() {
      return this.patrolDrawer
    },
    patrols: get('dashboard/patrols'),
    myEvents: get('dashboard/myEvents'),
    notMyEvents: get('dashboard/notMyEvents'),
    unassignedEvents: get('dashboard/unassignedEvents'),
    myClosedPendingAckCount: get('dashboard/myClosedPendingAckCount'),
    myEventSignalCount: get('dashboard/myEventSignalCount'),
    myEventsCount() {
      return this.myEvents.length
    },
    notMyEventsCount() {
      return this.notMyEvents.length
    },
    unassignedEventsCount() {
      return this.unassignedEvents.length
    },
    patrolCount() {
      return this.patrols && this.patrols.results ? this.patrols.results.length : 0
    },
    activePatrolsCount() {
      // Number of patrols with active tasks
      if (!this.patrols || !this.patrols.results) return 0
      return this.patrols.results.filter(patrol => patrol.tasks && patrol.tasks.length > 0).length
    },
    hasPatrolsWithErrors() {
      // Checks if there are patrols with errors (statuses lasting too long)
      if (!this.patrols || !this.patrols.results) return false
      return this.patrols.results.some(patrol => isPatrolDelayed(patrol))
    },
  },
  mounted() {
    // Set the initial dark mode state
    this.$vuetify.theme.dark = this.dark
  },
  watch: {
    dark(val) {
      this.$vuetify.theme.dark = val
    },
    myEventSignalCount(newCount, oldCount) {
      // Play sound only for growth of "My unread events" signals.
      const isEventDetailsRoute = this.$route?.name === 'Event'
      const nextCount = Number.isFinite(newCount) ? newCount : 0
      const previousCount = Number.isFinite(oldCount) ? oldCount : 0

      if (!isEventDetailsRoute && nextCount > previousCount) {
        playNotification()
      }
    },
  },
}
</script>

<style scoped>
/* Pulse animation for the patrols button when there are errors */
.v-btn--icon.pulse-active {
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(244, 67, 54, 0.4);
  }

  70% {
    box-shadow: 0 0 0 10px rgba(244, 67, 54, 0);
  }

  100% {
    box-shadow: 0 0 0 0 rgba(244, 67, 54, 0);
  }
}
</style>
